import { Flame, Dumbbell, TrendingUp, Music, Target } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Landing() {
  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-primary/5 blur-3xl opacity-20 pointer-events-none" />
      
      <div className="container mx-auto px-4 py-20 relative z-10">
        <div className="max-w-4xl mx-auto text-center space-y-12">
          {/* Logo */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="p-4 bg-primary/20 rounded-2xl">
              <Flame className="w-12 h-12 text-primary animate-pulse" />
            </div>
          </div>

          {/* Hero */}
          <div className="space-y-6">
            <h1 className="text-6xl md:text-7xl font-extrabold text-foreground tracking-tight">
              myFit<span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-emerald-400">Beats</span>
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground max-w-2xl mx-auto">
              Where music meets muscle. Track your workouts, crush your goals, and stay motivated every step of the way.
            </p>
          </div>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-8">
            <Button 
              size="lg" 
              className="text-xl px-12 py-7 min-w-[200px] bg-primary text-primary-foreground hover:bg-primary/90 font-bold shadow-lg shadow-primary/20 rounded-full"
              onClick={() => window.location.href = "/auth"}
              data-testid="button-get-started"
            >
              Get Started
            </Button>
          </div>

          {/* Features */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pt-8">
            <div className="p-6 rounded-2xl bg-card/30 border border-border/50 backdrop-blur-sm">
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center mb-4 mx-auto">
                <Dumbbell className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-lg font-bold text-foreground mb-2">Smart Tracking</h3>
              <p className="text-muted-foreground text-sm">
                Log every set, rep, and weight. Auto-suggests when to progress.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-card/30 border border-border/50 backdrop-blur-sm">
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center mb-4 mx-auto">
                <Music className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-lg font-bold text-foreground mb-2">Spotify Integration</h3>
              <p className="text-muted-foreground text-sm">
                Stay motivated with curated playlists or link your own Spotify favorites.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-card/30 border border-border/50 backdrop-blur-sm">
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center mb-4 mx-auto">
                <TrendingUp className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-lg font-bold text-foreground mb-2">Visual Progress</h3>
              <p className="text-muted-foreground text-sm">
                Visualize your weight, PRs, and speed with intuitive charts.
              </p>
            </div>
          </div>

          {/* Motivation section */}
          <div className="pt-12 pb-8">
            <div className="p-8 rounded-3xl bg-gradient-to-br from-primary/10 to-emerald-500/10 border border-primary/20">
              <div className="flex items-center justify-center gap-3 mb-4">
                <Target className="w-8 h-8 text-primary" />
                <h2 className="text-2xl font-bold text-foreground">Stay Motivated, Stay Consistent</h2>
              </div>
              <p className="text-muted-foreground max-w-xl mx-auto">
                Motivation fades, but progress doesn't have to. With personalized workout tracking, 
                visual milestones, and music that matches your energy, myFitBeats helps you 
                stay focused and excited about every session.
              </p>
            </div>
          </div>

          {/* Footer Links */}
          <div className="pt-8 border-t border-border/30">
            <div className="flex justify-center gap-6 text-sm text-muted-foreground">
              <a 
                href="/privacy" 
                className="hover:text-foreground transition-colors"
                data-testid="link-privacy"
              >
                Privacy Policy
              </a>
              <a 
                href="/terms" 
                className="hover:text-foreground transition-colors"
                data-testid="link-terms"
              >
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
